import React, { useEffect, useMemo, useRef, useState } from 'react';

import PropTypes from 'prop-types';

import styles from './Step2_LoadingRecommendations.module.scss';

const PHRASES = [
  'Understanding your desired goals…',
  'Analyzing effective solutions…',
  'Checking current market trends…',
  'Finalizing your recommended deals…',
];

const MIN_LOADING_MS = 1800;
const sleep = ms => new Promise(r => setTimeout(r, ms));

export default function Step2_LoadingRecommendations({
  draft,
  onBack,
  onSuccess,
  onFailGoToScratch,
  generateFn,
}) {
  const startedAtRef = useRef(Date.now());
  const [phase, setPhase] = useState(0);
  const [error, setError] = useState(null);

  const goalName = useMemo(() => {
    if (draft.goalKey === 'attract_new_customers')
      return 'Attract New Customers';
    if (draft.goalKey === 'increase_sales') return 'Increase Sales';
    if (draft.goalKey === 'fill_slow_periods') return 'Fill Slow Periods';
    return 'Your Goal';
  }, [draft.goalKey]);

  const progressPct = useMemo(() => {
    const base = (phase + 1) / PHRASES.length;
    return Math.min(95, Math.round(base * 100));
  }, [phase]);

  useEffect(() => {
    const t = setInterval(() => {
      setPhase(p => Math.min(PHRASES.length - 1, p + 1));
    }, 900);
    return () => clearInterval(t);
  }, []);

  useEffect(() => {
    let cancelled = false;

    (async () => {
      try {
        const recs = await generateFn(draft);
        if (cancelled) return;

        if (!Array.isArray(recs) || recs.length === 0) {
          throw new Error('No recommendations returned');
        }

        const elapsed = Date.now() - startedAtRef.current;
        if (elapsed < MIN_LOADING_MS) await sleep(MIN_LOADING_MS - elapsed);
        if (cancelled) return;

        onSuccess(recs);
      } catch (e) {
        if (cancelled) return;
        setError(e?.message || 'Failed to generate recommendations');
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [draft, generateFn, onSuccess]);

  return (
    <div className={styles.page}>
      <div className={styles.card}>
        {!error ? (
          <>
            <div className={styles.title}>Building Your Custom Coupons!</div>
            <div className={styles.sub}>
              Goal: <strong>{goalName}</strong>
            </div>

            <div className={styles.progressWrap} aria-label="Progress">
              <div className={styles.progressBar}>
                <div
                  className={styles.progressFill}
                  style={{ width: `${progressPct}%` }}
                />
              </div>
              <div className={styles.progressText}>{progressPct}%</div>
            </div>

            <div className={styles.stepsList} aria-label="Progressing steps">
              {PHRASES.map((p, idx) => {
                const done = idx < phase;
                const active = idx === phase;
                return (
                  <div
                    key={p}
                    className={`${styles.stepRow} ${
                      done ? styles.stepDone : ''
                    } ${active ? styles.stepActive : ''}`}
                  >
                    <span className={styles.stepDot} />
                    <span className={styles.stepText}>{p}</span>
                  </div>
                );
              })}
            </div>

            <div className={styles.loaderRow} aria-label="Loading">
              <span className={styles.dot} />
              <span className={styles.dot} />
              <span className={styles.dot} />
            </div>

            <div className={styles.actions}>
              <button
                type="button"
                className={styles.secondaryBtn}
                onClick={onBack}
              >
                Back
              </button>
            </div>
          </>
        ) : (
          <>
            <div className={styles.title}>
              Oops! Our AI is taking a quick break.
            </div>
            <div className={styles.errorText}>
              No worries — you can still create an amazing coupon from scratch.
            </div>

            <div className={styles.actions}>
              <button
                type="button"
                className={styles.secondaryBtn}
                onClick={onBack}
              >
                Go Back
              </button>
              <button
                type="button"
                className={styles.primaryBtn}
                onClick={onFailGoToScratch}
              >
                Create Coupon from Scratch
              </button>
            </div>

            <div className={styles.smallError}>{String(error)}</div>
          </>
        )}
      </div>
    </div>
  );
}

Step2_LoadingRecommendations.propTypes = {
  draft: PropTypes.object.isRequired,
  onBack: PropTypes.func.isRequired,
  onSuccess: PropTypes.func.isRequired,
  onFailGoToScratch: PropTypes.func.isRequired,
  generateFn: PropTypes.func.isRequired,
};
